/**
 * How the public site reads a jurisdiction's coverage status.
 *
 * This file used to carry the table of all 24 jurisdictions written by hand,
 * with `status`, `designTitle`, `version`, `validatedAt` and `levels` invented
 * at launch and a `TODO(12.4): wire to backend IAC` beside them. Meanwhile the
 * page published numbers no query had ever produced, which is the objective-5
 * violation this whole product exists to refuse.
 *
 * The geometry lives in `DEFAULT_GRID` of `@sica/ui` (it is brand identity) and
 * the counts arrive measured from `lib/coverage-client`. What stays here is the
 * derivation of the status and its copy, the only part the site owns.
 */

import { DEFAULT_GRID, type MapJurisdiction, type MapStatus } from '@sica/ui';
import type { LiveCoverageEntry } from '@/lib/coverage-client';

/** The four statuses the site paints; measuring left `progress` with no use. */
export type CoverageStatus = Extract<MapStatus, 'covered' | 'partial' | 'citable' | 'pending'>;

/**
 * The status comes out of the counts and never out of a declaration.
 *
 * It is counted over the statements that sit in an area and a year, which is
 * the coordinate the corpus is queried by. `citable` is a status of its own and
 * not a paler green: painting it as covered would promise narrowing by area and
 * year, which is exactly what that province cannot do yet.
 *
 * The rule is the product's rule because the legend is one legend, and it has
 * been wrong twice in the same direction. First `structured > 0` painted a whole
 * province that had one structured document and thirteen citable ones. Then
 * ADR-0035 made it a proportion — of the DOCUMENT's tier: Salta has a single
 * structured design, no citable ones, and painted covered while four of its
 * 1.514 statements sat in a year. The tier is how a document was labelled and
 * the coordinates are what its rows hold (ADR-0039). This is the public surface
 * where that promise reads as a commitment.
 */
export function coverageStatus(navigable: number, statements: number): CoverageStatus {
  if (statements === 0) return 'pending';
  if (navigable === 0) return 'citable';
  return navigable === statements ? 'covered' : 'partial';
}

export const STATUS_LABELS: Record<CoverageStatus, { label: string; description: string }> = {
  covered: {
    label: 'Navegable por área y año',
    description:
      'Todo lo cargado del diseño se ubica en un área y un año: se pide un grado y el documento contesta, con la cita y la página de cada enunciado.',
  },
  partial: {
    label: 'Navegable en parte',
    description:
      'Una parte del diseño ya se ubica en un área y un año. El resto está cargado con su cita resuelta, pero sin las coordenadas con las que se lo acota.',
  },
  citable: {
    label: 'Sólo texto citable',
    description:
      'El documento está cargado completo y cada enunciado resuelve su cita con página. Ninguno se ubica todavía en un área y un año, así que no hay por dónde acotarlo.',
  },
  pending: {
    label: 'Sin fuente asegurada',
    description:
      'Todavía no conseguimos el diseño vigente publicado por la jurisdicción. Decirlo es parte de no mentir sobre la cobertura.',
  },
};

/** The order the page explains the statuses in. */
export const STATUS_ORDER: readonly CoverageStatus[] = ['covered', 'partial', 'citable', 'pending'];

/**
 * La proporción en palabras, nunca un cero pelado.
 *
 * Una provincia sin nada promovido igual tiene su texto entero cargado y cada
 * cita resolviendo, y el cero en la primera línea de la ficha es lo que hacía
 * leer el beige punteado como ausencia (ADR-0035). Sin documentos, la
 * proporción no es cero: es indefinida, y decirlo así es la única lectura
 * honesta.
 */
function shareLabel(entry: LiveCoverageEntry): string {
  const { navigableStatements: navigable, statements } = entry;
  if (statements === 0) return 'Sin documentos cargados';
  const total = statements.toLocaleString('es-AR');
  if (navigable === 0) return `Ninguno de los ${total} enunciados cargados, todavía`;
  if (navigable === statements) return `Los ${total} enunciados cargados`;
  const percent = Math.round((navigable / statements) * 100);
  return `${navigable.toLocaleString('es-AR')} de ${total} enunciados (${percent} %)`;
}

/**
 * Una celda del mapa por cada jurisdicción medida. La fila nacional no es una
 * provincia y no se dibuja; una fila sin lugar en la grilla se descarta porque
 * no hay dónde ponerla.
 */
export function toMapJurisdictions(
  entries: readonly LiveCoverageEntry[],
): MapJurisdiction[] {
  return entries
    .filter((entry) => entry.type !== 'national')
    .flatMap((entry) => {
      const slot = DEFAULT_GRID[entry.jurisdictionId];
      if (!slot) return [];
      return [
        {
          id: entry.jurisdictionId,
          code: slot.code,
          name: entry.name,
          status: coverageStatus(entry.navigableStatements, entry.statements),
          gridPosition: slot.gridPosition,
          detail: [
            { term: 'Enunciados con área y año', value: shareLabel(entry) },
            { term: 'Con texto citable', value: entry.citableStatements.toLocaleString('es-AR') },
          ],
        },
      ];
    });
}

/** Cuántas jurisdicciones hay en cada estado, sobre lo medido. */
export function countByStatus(
  jurisdictions: readonly MapJurisdiction[],
): Record<CoverageStatus, number> {
  const counts: Record<CoverageStatus, number> = { covered: 0, partial: 0, citable: 0, pending: 0 };
  for (const jurisdiction of jurisdictions) {
    // `STATUS_ORDER` es la lista de estados que el sitio pinta: derivar de ella
    // en vez de repetirla en un `if` es lo que hace que un estado nuevo entre en
    // el conteo sin que nadie se acuerde de tocar esta línea.
    if ((STATUS_ORDER as readonly string[]).includes(jurisdiction.status)) {
      counts[jurisdiction.status as CoverageStatus] += 1;
    }
  }
  return counts;
}
